import { Injectable, OnDestroy, computed, effect, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import {
  clearAiAssistant,
  closePanel,
  createConversation,
  loadConversations,
  loadNextConversationsPage,
  openPanel,
  selectConversation,
  sendConsentDecision,
  sendMessage,
  setPanelSize,
  startNewConversation,
  stopStream,
} from '@core/stores/ai-assistant/ai-assistant.actions';
import {
  selectActiveConversationId,
  selectChargerNameIfLoaded,
  selectComposerDisabled,
  selectConversationListVm,
  selectConversationsHasMore,
  selectConversationsLoading,
  selectIsFullscreen,
  selectIsNewChat,
  selectIsPanelOpen,
  selectIsStreaming,
  selectMessages,
  selectMessagesLoading,
  selectPanelSize,
  selectPendingConsent,
  selectPromptPresets,
  selectPromptPresetsLoading,
  selectShowTypingIndicator,
  selectSitesIfLoaded,
  selectStatus,
  selectStreamError,
  selectVehicleDetailsIfLoaded,
} from '@core/stores/ai-assistant/ai-assistant.selectors';
import { ChargerNamesStore } from '@core/stores/charger-names/charger-names.store';
import { selectRouterState } from '@core/stores/router/router.selectors';
import { TenantsStore } from '@core/stores/tenants/tenants.store';

import { AiAssistantUiContext, prependAiAssistantUiContext } from '@domain/ai-assistant/ai-assistant-ui-context';
import { AiAssistantPromptPreset, AiToolConsentDecision } from '@domain/ai-assistant/ai-assistant.models';

import { AssetDetailsDialogStore } from '@shared/stores/asset-details-dialog/asset-details-dialog.store';
import { getPathParamOfRoute } from '@shared/utils/router-state';

@Injectable()
export class AiAssistantService implements OnDestroy {
  readonly #store = inject(Store);
  readonly #chargerNamesStore = inject(ChargerNamesStore);
  readonly #tenantsStore = inject(TenantsStore);
  readonly #assetDetailsDialogStore = inject(AssetDetailsDialogStore);

  // ---------- Signals ----------

  public readonly panelSize = this.#store.selectSignal(selectPanelSize);
  public readonly isPanelOpen = this.#store.selectSignal(selectIsPanelOpen);
  public readonly isFullscreen = this.#store.selectSignal(selectIsFullscreen);
  public readonly messages = this.#store.selectSignal(selectMessages);
  public readonly messagesLoading = this.#store.selectSignal(selectMessagesLoading);
  public readonly status = this.#store.selectSignal(selectStatus);
  public readonly isStreaming = this.#store.selectSignal(selectIsStreaming);
  public readonly showTypingIndicator = this.#store.selectSignal(selectShowTypingIndicator);
  public readonly composerDisabled = this.#store.selectSignal(selectComposerDisabled);
  public readonly pendingConsent = this.#store.selectSignal(selectPendingConsent);
  public readonly streamError = this.#store.selectSignal(selectStreamError);
  public readonly conversationListVm = this.#store.selectSignal(selectConversationListVm);
  public readonly conversationsLoading = this.#store.selectSignal(selectConversationsLoading);
  public readonly conversationsHasMore = this.#store.selectSignal(selectConversationsHasMore);
  public readonly isNewChat = this.#store.selectSignal(selectIsNewChat);
  public readonly promptPresets = this.#store.selectSignal(selectPromptPresets);
  public readonly promptPresetsLoading = this.#store.selectSignal(selectPromptPresetsLoading);
  readonly #routerState = this.#store.selectSignal(selectRouterState);
  readonly #sites = this.#store.selectSignal(selectSitesIfLoaded);
  readonly #chargerNameFromActivity = this.#store.selectSignal(selectChargerNameIfLoaded);
  readonly #vehicleDetails = this.#store.selectSignal(selectVehicleDetailsIfLoaded);
  readonly #assetDetailsDialogContext = this.#assetDetailsDialogStore.context;
  readonly #assetDetailsDialogEvseDetails = this.#assetDetailsDialogStore.details;
  readonly #baseUiContext = computed((): AiAssistantUiContext | undefined => {
    const routerState = this.#routerState();
    const dialogContext = this.#assetDetailsDialogContext();
    const evseDetails = this.#assetDetailsDialogEvseDetails();
    const dialogEvseDetails = dialogContext?.evseId === evseDetails?.evseId ? evseDetails : undefined;
    const siteId = dialogContext?.siteId ?? getPathParamOfRoute(routerState, 'siteId') ?? undefined;
    const chargerId = dialogEvseDetails?.chargerId ?? getPathParamOfRoute(routerState, 'chargerId') ?? undefined;
    const vehicleId = getPathParamOfRoute(routerState, 'vehicleId') ?? undefined;
    const evseId = dialogEvseDetails?.evseId;

    if (!(siteId || chargerId || evseId || vehicleId)) {
      return undefined;
    }

    const siteNamesById = Object.fromEntries(
      [...(this.#tenantsStore.sites() ?? []), ...(this.#sites() ?? [])].map((site) => [site.id, site.name])
    );
    const chargerNameFromActivity = this.#chargerNameFromActivity();
    const vehicleDetails = this.#vehicleDetails();

    return {
      ...(siteId ? { siteId } : {}),
      ...(siteId && siteNamesById[siteId] ? { siteName: siteNamesById[siteId] } : {}),
      ...(chargerId ? { chargerId } : {}),
      ...(chargerId ? { chargerName: dialogEvseDetails?.chargerName ?? chargerNameFromActivity } : {}),
      ...(evseId ? { evseId } : {}),
      ...(dialogEvseDetails?.evseName ? { evseName: dialogEvseDetails.evseName } : {}),
      ...(vehicleId ? { vehicleId } : {}),
      ...(vehicleId && vehicleDetails?.id === vehicleId ? { vehicleName: vehicleDetails.name } : {}),
    };
  });
  public readonly uiContext = computed(() => {
    const context = this.#baseUiContext();
    if (!context?.siteId || !context.chargerId || context.chargerName) {
      return context;
    }
    return {
      ...context,
      chargerName: this.#chargerNamesStore.chargerNameForSite(context.siteId, context.chargerId),
    };
  });

  readonly #activeConversationId = this.#store.selectSignal(selectActiveConversationId);

  // ---------- Actions ----------

  constructor() {
    effect(() => {
      const context = this.#baseUiContext();
      if (this.isPanelOpen() && context?.siteId && context.chargerId) {
        this.#chargerNamesStore.ensureChargerNamesLoaded(context.siteId);
      }
    });
  }

  public openSmall(): void {
    this.#store.dispatch(openPanel({ size: 'small' }));
  }

  public startNew(): void {
    this.#store.dispatch(startNewConversation());
  }

  public expand(): void {
    this.#store.dispatch(setPanelSize({ size: 'fullscreen' }));
    this.#store.dispatch(loadConversations({}));
  }

  public openSideMenu(): void {
    this.#store.dispatch(loadConversations({}));
  }

  public loadMoreConversations(): void {
    this.#store.dispatch(loadNextConversationsPage());
  }

  public collapse(): void {
    this.#store.dispatch(setPanelSize({ size: 'small' }));
  }

  public close(): void {
    this.#store.dispatch(stopStream());
    this.#store.dispatch(closePanel());
  }

  public send(message: string, uiContext: AiAssistantUiContext | undefined): void {
    const conversationId = this.#activeConversationId();
    const messageWithContext = prependAiAssistantUiContext(message, uiContext);
    if (conversationId) {
      this.#store.dispatch(sendMessage({ conversationId, message: messageWithContext }));
    } else {
      this.#store.dispatch(createConversation({ pendingMessage: messageWithContext }));
    }
  }

  public runPromptPreset(promptPreset: AiAssistantPromptPreset): void {
    const contextWithPromptPreset: AiAssistantUiContext = {
      ...this.uiContext(),
      promptPresetName: promptPreset.name,
    };
    const messageWithContext = prependAiAssistantUiContext(promptPreset.prompt, contextWithPromptPreset);
    this.#store.dispatch(createConversation({ pendingMessage: messageWithContext }));
  }

  public sendConsent(consentId: string, decision: AiToolConsentDecision): void {
    const conversationId = this.#activeConversationId();
    if (!conversationId) return;
    this.#store.dispatch(sendConsentDecision({ conversationId, consentId, decision }));
  }

  public selectConversation(conversationId: string): void {
    this.#store.dispatch(selectConversation({ conversationId }));
  }

  public ngOnDestroy(): void {
    this.#store.dispatch(clearAiAssistant());
  }
}
